import { useCallback, useLayoutEffect, useRef } from 'react';

/**
 * Returns a stable-identity function that always invokes the LATEST `fn`.
 *
 * For event-time-only callbacks that are rebuilt every render (closing over
 * fresh state), so they can be passed to a `React.memo` child without
 * defeating the memo — the returned identity never changes, but calls always
 * reach the newest closure (the "useEvent" pattern). Do NOT use the result
 * during render (identity is stable, so it can't signal render-time changes).
 */
export function useLatestCallback<Args extends unknown[], R>(
  fn: (...args: Args) => R,
): (...args: Args) => R {
  const ref = useRef(fn);
  useLayoutEffect(() => {
    ref.current = fn;
  });
  return useCallback((...args: Args) => ref.current(...args), []);
}
